import { Component, OnDestroy, OnInit } from '@angular/core';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { FileUploaderInstance, FileUploaderService } from 'app/store/services/file-uploader.service';
import { Router } from '@angular/router';
import { DataSourceParams, ETableColumnType, ITableColumn, ITableDataSource } from 'app/store/models/table.model';
import { ApiDataSource } from 'app/datasources/api.datasrouce';
import { IUploadHistory } from 'app/store/models/upload-history.model';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ContactsService } from 'app/store/services/contacts.service';
import { openSnackbar } from '../../store/actions/page.action';
import { SessionService } from '../../store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { IUserSessionData } from '../../store/models/user.model';
import { TimezonePipe } from '../../shared/ui/pipes/timezone.pipe';
import { UtilitiesService } from '../../store/services/utilities.servive';

@Component({
  selector: 'app-upload-contact',
  templateUrl: './upload-contact.component.html',
  styleUrls: ['./upload-contact.component.scss'],
})
export class UploadContactComponent implements OnInit, OnDestroy {
  static apiEndPoint = environment.apiUrl + '/v1/contacts/upload';

  uploadButtonProperties: Button = {
    text: 'Upload',
    color: ButtonColor.ORANGE,
    size: ButtonSize.SMALL,
    onClick: () => this.fileUploaderService.selectFile(),
  };
  tableDataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'uploaded_at',
    sortDir: 'desc',
    filters: {},
  };
  tableColumns: ITableColumn<IUploadHistory>[];
  dataSource: ITableDataSource;
  private uploaderInstance: FileUploaderInstance;
  private readonly destroy$ = new Subject<boolean>();

  private sessionData: IUserSessionData;

  constructor(
    private store: Store<IReducers>,
    private fileUploaderService: FileUploaderService,
    private contactsService: ContactsService,
    private router: Router,
    private http: HttpClient,
    private readonly sessionService: SessionService,
    private readonly utilService: UtilitiesService,
  ) {
    // this.tableDataSourceParams.page = 10.5;

    this.dataSource = new ApiDataSource<IUploadHistory>(
      this.http,
      UploadContactComponent.apiEndPoint,
    );
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;

        this.uploaderInstance = this.fileUploaderService.init({
          accept: ['text/csv', '.xls', '.xlsx'],
          buttonLabel: 'Select File',
          dialogTitle: 'Upload file',
          requestUri: UploadContactComponent.apiEndPoint,
          showDropZone: true,
          allowMultiple: true,
          param: 'contacts_file',
          maxFiles: 1,
          maxFileSize: '4MB',
          showWarnings: false,
          customWarning: 'Supported file formats are <b>.csv</b> and <b>.xls</b><br/>Maximum file size is <b>4MB</b>. If your <b>.xls</b> file has many columns, we recommend converting it to <b>.csv</b>.',
          onComplete: (response: { file_id: number }) => {
            if (response && response.file_id) {
              this.router.navigate([
                '/user_contacts',
                'process_upload',
                response.file_id,
              ]);
            }
          },
          onError: res => {
            let message = res.error;
            if (message && message.message) {
              const messageDecoded = JSON.parse(message.message);
              message = '';
              for (const key in messageDecoded) {
                if (messageDecoded.hasOwnProperty(key)) {
                  message += messageDecoded[key];
                  break;
                }
              }
            }
            this.store.dispatch(
              openSnackbar({
                snackbarData: {
                  data: {
                    message,
                    type: 'error',
                  },
                },
              }),
            );
          },
        });
        this.setColumns();
      });
  }

  setColumns() {
    this.tableColumns = [
      {
        id: 'file_id',
        title: 'File ID',
      },
      {
        id: 'file_name',
        title: 'File Name',
      },
      {
        id: 'uploaded_at',
        title: 'Uploaded At',
        sortable: true,
        renderer: row => {
          const pipe = new TimezonePipe(this.utilService);
          return `${pipe.transform(
            row.uploaded_at,
            'MM/DD/YY hh:mm:ss',
            this.sessionData.time_zone,
          )}`;
        },
      },
      {
        id: 'file_status',
        title: 'Status',
        sortable: true,
      },
      {
        id: 'file_report',
        title: 'Report',
        type: ETableColumnType.BUTTON,
        getButtonProperties: (row: IUploadHistory) => {
          return {
            color: ButtonColor.WHITE,
            text: row.file_report ? 'Download Report' : 'Not Available',
            buttonDisabled: () => !row.file_report,
            size: ButtonSize.SMALL,
            onClick: () => this.downloadReport(row),
          };
        },
      },
    ];
  }

  downloadReport(row) {
    this.contactsService.getDownload(row.file_id).subscribe(response => {
      const resp = response as HttpResponse<Blob>;

      let attachementName = row.file_name;

      const despositionHeader = resp.headers.get('content-disposition');

      if (despositionHeader) {
        const despositionHeaderParts = despositionHeader.split(';');
        if (despositionHeaderParts.length > 1) {
          const fileNamePart = despositionHeaderParts[1].split('=');
          if (fileNamePart.length > 1) {
            attachementName = fileNamePart[1].replace('"', '').replace('"', '');
          }
        }
      }
      const dataType = response.type;
      const binaryData = [];
      binaryData.push(resp.body);
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(
        new Blob(binaryData, { type: dataType }),
      );

      downloadLink.setAttribute('download', `${attachementName}`);
      document.body.appendChild(downloadLink);
      downloadLink.click();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
